import { useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { X } from 'lucide-react';
import { Button } from './Button.jsx';

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** The one easing curve the system uses for anything meaningful. */
const EASE = [0.2, 0.7, 0.2, 1];

/**
 * FE-13: modals trap focus and close on Escape. Focus also returns to whatever
 * opened the dialog, so a keyboard user is not dropped at the top of the page.
 */
export function Modal({ open, onClose, title, description, blurb, children, footer, size = 'md' }) {
  description = description ?? blurb;
  const panelRef = useRef(null);
  const previouslyFocused = useRef(null);

  const handleKeyDown = useCallback(
    (event) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose?.();
        return;
      }
      if (event.key !== 'Tab') return;

      const focusable = panelRef.current?.querySelectorAll(FOCUSABLE);
      if (!focusable?.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      // Wrap at both ends: the page behind is still rendered and still tabbable.
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    },
    [onClose],
  );

  useEffect(() => {
    if (!open) return undefined;

    previouslyFocused.current = document.activeElement;
    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';

    const timer = setTimeout(() => {
      const focusable = panelRef.current?.querySelectorAll(FOCUSABLE);
      (focusable?.[0] ?? panelRef.current)?.focus();
    }, 0);

    return () => {
      clearTimeout(timer);
      document.body.style.overflow = overflow;
      previouslyFocused.current?.focus?.();
    };
  }, [open]);

  const widths = { sm: 'max-w-md', md: 'max-w-lg', lg: 'max-w-2xl' };

  return createPortal(
    <AnimatePresence>
      {open && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center px-0 sm:items-center sm:px-4"
          onKeyDown={handleKeyDown}
        >
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
            className="absolute inset-0 backdrop-blur-sm"
            style={{ background: 'rgb(0 0 0 / 0.45)' }}
            onClick={onClose}
          />

          <motion.div
            ref={panelRef}
            role="dialog"
            aria-modal="true"
            aria-label={title}
            tabIndex={-1}
            initial={{ opacity: 0, y: 12, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.97 }}
            transition={{ duration: 0.22, ease: EASE }}
            className={`relative z-10 max-h-[88vh] w-full overflow-y-auto rounded-t-2xl sm:rounded-2xl ${widths[size] ?? widths.md}`}
            style={{
              background: 'rgb(var(--surface))',
              border: '1px solid rgb(var(--line))',
              boxShadow: 'var(--shadow-lg)',
            }}
          >
            <div className="flex items-start justify-between gap-4 p-5 sm:p-6 sm:pb-5">
              <div className="min-w-0">
                <h3 className="text-xl font-semibold tracking-tight text-ink">{title}</h3>
                {description && (
                  <p className="mt-1 text-sm leading-relaxed text-muted">{description}</p>
                )}
              </div>
              <button type="button" onClick={onClose} className="btn-icon shrink-0" aria-label="Close dialog">
                <X className="h-[18px] w-[18px]" />
              </button>
            </div>

            <div className="px-5 pb-5 sm:px-6">{children}</div>

            {footer && (
              <div
                className="flex flex-wrap justify-end gap-2 border-t px-5 py-4 sm:px-6"
                style={{ borderColor: 'rgb(var(--line-soft))' }}
              >
                {footer}
              </div>
            )}
          </motion.div>
        </div>
      )}
    </AnimatePresence>,
    document.body,
  );
}

/**
 * FE-10: every destructive action confirms first and states the consequence in
 * plain words. `consequence` is required rather than optional — a dialog that
 * only asks "Are you sure?" gives someone nothing to be sure about.
 */
export function ConfirmDialog({
  open, onClose, onConfirm, title, consequence,
  confirmLabel = 'Confirm', variant = 'danger', loading = false,
}) {
  return (
    <Modal
      open={open}
      onClose={loading ? () => {} : onClose}
      title={title}
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={loading}>Cancel</Button>
          <Button variant={variant} onClick={onConfirm} loading={loading}>{confirmLabel}</Button>
        </>
      }
    >
      <p className="text-sm leading-relaxed text-ink-soft">{consequence}</p>
    </Modal>
  );
}

export default Modal;
